// src/features/boe/utils/staffSnapshotUtils.js

import React, { useMemo } from "react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

import { HoverPopover } from "../../../shared/ui/overlay/HoverPopover.jsx";
import { formatFiscalYearFromStart } from "../../../shared/utils/fiscalYearUtils.js";

/**
 * Preferred ordering of staffing role labels for display.
 * Any role not in this list will appear after known roles, sorted alphabetically.
 */
export const POSITION_ORDER = [
  "Administrative",
  "Teachers",
  "Paras",
  "In Class Tutors / Paras",
  "Substitutes",
  "Custodians",
  "Clerical",
];

/**
 * Group staffing roles into chart-friendly families.
 *
 * The raw position keys come from budget docs and are intentionally human
 * labels, so this helper uses conservative text matching rather than requiring
 * every source field to carry new metadata.
 *
 * @param {string} roleKey
 * @returns {string}
 */
export function getStaffPositionGroup(roleKey) {
  const role = String(roleKey || "").toLowerCase();

  if (!role || role === "total") return "staffTotals";
  if (role.includes("admin")) return "staffAdministration";
  if (role.includes("teacher")) return "staffCertified";
  if (role.includes("tutor") || role.includes("para")) {
    return "staffInstructionalSupport";
  }
  if (role.includes("substitute")) return "staffSubstitutes";
  if (role.includes("custodian")) return "staffOperations";
  if (role.includes("clerical") || role.includes("secretar")) {
    return "staffClerical";
  }

  return "staffOther";
}

/**
 * Sort role keys using POSITION_ORDER first, then alphabetical.
 *
 * @param {string[]} keys
 * @returns {string[]}
 */
export function sortPositionKeys(keys) {
  const orderMap = new Map(POSITION_ORDER.map((k, i) => [k, i]));
  return [...(keys || [])].sort((a, b) => {
    const ia = orderMap.has(a) ? orderMap.get(a) : 9999;
    const ib = orderMap.has(b) ? orderMap.get(b) : 9999;
    if (ia !== ib) return ia - ib;
    return String(a).localeCompare(String(b));
  });
}

/**
 * Build a key safe for use as a chart series "key".
 *
 * IMPORTANT (behavioral note):
 * This normalizes aggressively (non-alphanumeric -> underscores), so *different*
 * labels can collide into the same slug (e.g., punctuation/spacing variants).
 * If two roles ever slugify to the same value, chart toggles can appear coupled
 * because the series `key` becomes identical.
 *
 * @param {string} s
 * @returns {string}
 */
export function slugifyKey(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/**
 * Convert a year key (string) into a usable numeric year.
 *
 * We only accept:
 * - Finite numbers
 * - Reasonable 4-digit years in a bounded range
 *
 * @param {string} yearKey
 * @returns {number|null} Year as number, or null if invalid.
 */
export function toYearNumber(yearKey) {
  const n = Number(yearKey);
  if (!Number.isFinite(n)) return null;
  if (n < 2000 || n > 2100) return null;
  return n;
}

/**
 * Determine if a section has any non-zero FTE values in its Positions block.
 * Used for year availability checks.
 *
 * @param {any|undefined|null} section
 * @returns {boolean}
 */
export function hasAnyNonZeroPositions(section) {
  const block = section?.Positions;
  if (!block || typeof block !== "object") return false;

  return Object.values(block).some(
    (v) => typeof v === "number" && Number.isFinite(v) && v !== 0
  );
}

/**
 * Availability across ALL programs, based on Positions (FTE).
 *
 * A year is marked available for a given section (Actual/Approved/Proposed) if
 * ANY program has any non-zero Positions in that section for that year.
 *
 * @param {Object<string, any>} programDocs - programId -> Firestore doc data
 * @returns {Map<number, {hasActual: boolean, hasApproved: boolean, hasProposed: boolean}>}
 */
export function buildYearAvailability(programDocs) {
  const byYear = new Map(); // year -> { hasActual, hasApproved, hasProposed }

  const ensure = (y) => {
    if (!byYear.has(y)) {
      byYear.set(y, {
        hasActual: false,
        hasApproved: false,
        hasProposed: false,
      });
    }
    return byYear.get(y);
  };

  Object.values(programDocs || {}).forEach((docData) => {
    if (!docData || typeof docData !== "object") return;

    Object.keys(docData).forEach((yearKey) => {
      const y = toYearNumber(yearKey);
      if (!y) return;

      const yearObj = docData[yearKey] || {};
      const slot = ensure(y);

      if (hasAnyNonZeroPositions(yearObj?.Actual)) slot.hasActual = true;
      if (hasAnyNonZeroPositions(yearObj?.Approved)) slot.hasApproved = true;
      if (hasAnyNonZeroPositions(yearObj?.Proposed)) slot.hasProposed = true;
    });
  });

  return byYear;
}

/**
 * List years that have data for a given section (Approved/Actual/Proposed),
 * based on the cross-program availability map.
 *
 * @param {Map<number, {hasActual: boolean, hasApproved: boolean, hasProposed: boolean}>} yearAvail
 * @param {"Approved"|"Actual"|"Proposed"} sectionKey
 * @returns {number[]}
 */
export function getYearsForSection(yearAvail, sectionKey) {
  const years = [];
  if (!yearAvail) return years;

  yearAvail.forEach((slot, y) => {
    if (!slot) return;
    if (sectionKey === "Approved" && slot.hasApproved) years.push(y);
    if (sectionKey === "Actual" && slot.hasActual) years.push(y);
    if (sectionKey === "Proposed" && slot.hasProposed) years.push(y);
  });

  years.sort((a, b) => a - b);
  return years;
}

/**
 * Sum a specific role across all programs for a given year + section.
 *
 * @param {Object<string, any>} programDocs
 * @param {Object} args
 * @param {number} args.year
 * @param {"Approved"|"Actual"|"Proposed"} args.sectionKey
 * @param {string} args.roleKey
 * @returns {number}
 */
export function sumRoleAcrossPrograms(
  programDocs,
  { year, sectionKey, roleKey }
) {
  let sum = 0;
  Object.values(programDocs || {}).forEach((docData) => {
    const v = docData?.[String(year)]?.[sectionKey]?.Positions?.[roleKey];
    if (typeof v === "number" && Number.isFinite(v)) sum += v;
  });
  return sum;
}

/**
 * Sum a specific salary role across all programs for a given year + section.
 *
 * @param {Object<string, any>} programDocs
 * @param {Object} args
 * @param {number} args.year
 * @param {"Approved"|"Actual"|"Proposed"} args.sectionKey
 * @param {string} args.roleKey
 * @returns {number}
 */
export function sumSalaryAcrossPrograms(
  programDocs,
  { year, sectionKey, roleKey }
) {
  let sum = 0;
  Object.values(programDocs || {}).forEach((docData) => {
    const v = docData?.[String(year)]?.[sectionKey]?.Salaries?.[roleKey];
    if (typeof v === "number" && Number.isFinite(v)) sum += v;
  });
  return sum;
}

/**
 * Compute average salary per FTE for a role (across all programs) for a year+section.
 *
 * @param {Object<string, any>} programDocs
 * @param {Object} args
 * @param {number} args.year
 * @param {"Approved"|"Actual"|"Proposed"} args.sectionKey
 * @param {string} args.roleKey
 * @returns {number|null}
 */
export function avgSalaryPerFteAcrossPrograms(
  programDocs,
  { year, sectionKey, roleKey }
) {
  const fte = sumRoleAcrossPrograms(programDocs, { year, sectionKey, roleKey });
  if (!Number.isFinite(fte) || fte === 0) return null;

  const sal = sumSalaryAcrossPrograms(programDocs, {
    year,
    sectionKey,
    roleKey,
  });
  if (!Number.isFinite(sal)) return null;

  return sal / fte;
}

/**
 * Collect all role keys present across all programs for a set of years and section.
 *
 * @param {Object<string, any>} programDocs
 * @param {number[]} years
 * @param {"Approved"|"Actual"|"Proposed"} sectionKey
 * @returns {string[]}
 */
export function collectRoleKeysForYears(programDocs, years, sectionKey) {
  const set = new Set();

  Object.values(programDocs || {}).forEach((docData) => {
    (years || []).forEach((y) => {
      const positions = docData?.[String(y)]?.[sectionKey]?.Positions;
      if (!positions || typeof positions !== "object") return;
      Object.keys(positions).forEach((k) => set.add(k));
    });
  });

  return sortPositionKeys(Array.from(set));
}

/**
 * Build chart payload for a single section (Approved/Actual/Proposed).
 *
 * @param {Object} args
 * @param {Object<string, any>} args.programDocs
 * @param {Map<number, {hasActual: boolean, hasApproved: boolean, hasProposed: boolean}>} args.yearAvail
 * @param {"Approved"|"Actual"|"Proposed"} args.sectionKey
 * @returns {{labels: string[], series: Array<Object>}}
 */
export function buildStaffChartForSection({
  programDocs,
  yearAvail,
  sectionKey,
}) {
  const years = getYearsForSection(yearAvail, sectionKey);
  if (!years.length) return { labels: [], series: [] };

  const roleKeys = collectRoleKeysForYears(programDocs, years, sectionKey);
  if (!roleKeys.length) return { labels: [], series: [] };

  const labels = years.map((y) => formatFiscalYearFromStart(y));

  const totalSeries = {
    key: "Total",
    label: "Total Staff (FTE)",
    data: years.map((y) =>
      roleKeys.reduce(
        (acc, roleKey) =>
          acc +
          sumRoleAcrossPrograms(programDocs, { year: y, sectionKey, roleKey }),
        0
      )
    ),
    group: "staffTotals",
    valueType: "count",
    defaultVisible: false,
  };

  const roleSeries = roleKeys.map((roleKey) => {
    const key = slugifyKey(roleKey);
    return {
      key,
      label: roleKey,
      data: years.map((y) =>
        sumRoleAcrossPrograms(programDocs, { year: y, sectionKey, roleKey })
      ),
      group: getStaffPositionGroup(roleKey),
      valueType: "count",
      defaultVisible: false,
    };
  });

  return { labels, series: [totalSeries, ...roleSeries] };
}

/**
 * Render a consistent two-line table header:
 * - Top line: FY label
 * - Second line: section label (Actual / Approved / Proposed)
 *
 * @param {string} fyLabel
 * @param {string} sectionLabel
 * @returns {JSX.Element}
 */
export function buildTwoLineHeader(fyLabel, sectionLabel) {
  return (
    <div className="leading-tight text-center whitespace-nowrap">
      <div className="font-semibold">{fyLabel}</div>
      <div>{sectionLabel}</div>
    </div>
  );
}

/**
 * Attach "Change" and "% Change" to a row based on the last two displayed columns.
 *
 * IMPORTANT:
 * - This mutates the passed-in row object (by design, consistent with other table builders).
 * - If the prior value is 0, percentIncrease is set to null to avoid Infinity.
 *
 * @param {Object} row - Row object being built for CommonTable.
 * @param {Array<{colKey: string}>} metaList - Column metadata for the visible window.
 * @returns {Object} The same row object (mutated).
 */
export function attachIncreaseFields(row, metaList) {
  if (!metaList || metaList.length < 2) return row;

  const prevMeta = metaList[metaList.length - 2];
  const currMeta = metaList[metaList.length - 1];

  const prevVal = row[prevMeta.colKey];
  const currVal = row[currMeta.colKey];

  if (typeof prevVal === "number" && typeof currVal === "number") {
    const diff = currVal - prevVal;
    row.increase = diff;
    row.percentIncrease = prevVal !== 0 ? diff / prevVal : null;
  }

  return row;
}

/**
 * Format FTE values for UI display.
 *
 * @param {number|null|undefined} val
 * @returns {string}
 */
export function formatFte(val) {
  if (val == null || !Number.isFinite(val)) return "—";
  return val.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

/**
 * @typedef {Object} BreakdownItem
 * @property {string} programId
 * @property {number} value
 */

/**
 * @typedef {Object<string, BreakdownItem[]>} RowBreakdownByColKey
 * Maps a column key (e.g. "approved_2025") to a list of program contributions.
 */

/**
 * Renders a role label with an info icon that shows a popover listing
 * the programs contributing to this role.
 *
 * @param {Object} props
 * @param {string} props.label
 * @param {RowBreakdownByColKey} props.breakdown
 * @param {Array<{colKey: string}>} props.windowYearsMeta
 * @param {Object<string, {fullName: string|null}>} props.programMetaById
 * @returns {JSX.Element}
 */
export function RoleLabelWithProgramsPopover({
  label,
  breakdown,
  windowYearsMeta,
  programMetaById,
}) {
  const hasAny = breakdown && Object.keys(breakdown).length > 0;

  const lastMeta = windowYearsMeta?.[windowYearsMeta.length - 1];
  const lastColKey = lastMeta?.colKey;

  const programIdSet = useMemo(() => {
    const set = new Set();
    if (!breakdown) return set;

    Object.values(breakdown).forEach((arr) => {
      (arr || []).forEach((x) => {
        if (x?.programId) set.add(x.programId);
      });
    });

    return set;
  }, [breakdown]);

  const list = useMemo(() => {
    const ids = Array.from(programIdSet);

    const latestMap = new Map();
    (breakdown?.[lastColKey] || []).forEach((x) => {
      if (x?.programId) latestMap.set(x.programId, x.value);
    });

    const rows = ids.map((programId) => {
      const fullName = programMetaById?.[programId]?.fullName;
      const programLabel = fullName ? `${programId} - ${fullName}` : programId;

      const latestVal = latestMap.has(programId) ? latestMap.get(programId) : 0;

      return { programId, programLabel, value: latestVal };
    });

    rows.sort((a, b) => {
      if (b.value !== a.value) return b.value - a.value;
      return String(a.programId).localeCompare(String(b.programId));
    });

    return rows;
  }, [programIdSet, breakdown, lastColKey, programMetaById]);

  const content = (
    <div className="p-3 text-xs">
      <div className="font-semibold text-sm text-gray-900 mb-2">{label}</div>

      {!list.length ? (
        <div className="text-gray-500 italic">No programs found.</div>
      ) : (
        <ul className="space-y-0.5">
          {list.map((x) => (
            <li
              key={`${label}-${x.programId}`}
              className="flex justify-between gap-4"
            >
              <span className="text-gray-700">{x.programLabel}</span>
              <span className="text-gray-900 font-medium">
                {formatFte(x.value)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <div className="relative flex items-center min-h-[22px] pr-6">
      <span className="leading-tight">{label}</span>

      {hasAny && (
        <div className="absolute right-1 top-1/2 -translate-y-1/2">
          <HoverPopover
            content={content}
            popoverClassName="text-gray-800"
            maxWidth={520}
          >
            <InformationCircleIcon
              className="w-4 h-4 text-gray-500 hover:text-gray-800 cursor-pointer"
              aria-label={`Show programs for ${label}`}
            />
          </HoverPopover>
        </div>
      )}
    </div>
  );
}
